<template>
  <div class="payout-history-table">
    <CCard class="border-0 shadow-sm">
      <CCardHeader class="bg-white border-bottom">
        <h5 class="mb-0">
          <translate translate-context="payout">Storico Payout</translate>
        </h5>
      </CCardHeader>
      <CCardBody class="p-0">
        <CDataTable
          :items="payouts.results"
          :fields="fields"
          hover
          :no-items-view="{ noResults: $pgettext('payout', 'Nessun payout trovato') }"
        >
          <!-- Data richiesta con expand -->
          <template #data_richiesta="{item}">
            <td>
              <div class="d-flex align-items-center">
                <CButton
                  color="link"
                  size="sm"
                  class="p-0 mr-2"
                  @click="toggleExpand(item)"
                >
                  <CIcon 
                    :name="isExpanded(item) ? 'cipChevronDown' : 'cipChevronRight'" 
                    size="sm"
                  />
                </CButton>
                <div>
                  <div class="font-weight-bold">{{ item.created_at | formatDate }}</div>
                  <div class="text-muted small" v-if="item.events_count">
                    {{ item.events_count }} 
                    <translate translate-context="payout" :translate-n="item.events_count" translate-plural="eventi">evento</translate>
                  </div>
                </div>
              </div>
            </td>
          </template>

          <!-- Importo -->
          <template #importo="{item}">
            <td>
              <div class="font-weight-bold">
                {{ item.paid_net | currency(currency) }}
              </div>
              <div class="text-muted small">
                <translate translate-context="payout">Gross</translate>: {{ item.paid_gross | currency(currency) }}
              </div>
              <div class="text-muted small" v-if="item.photos_sold">
                {{ item.photos_sold }} <translate translate-context="payout">photos</translate>
              </div>
            </td>
          </template>

          <!-- Stato -->
          <template #stato="{item}">
            <td>
              <CBadge :color="getStatusColor(item.status)" class="px-3 py-1">
                {{ getStatusLabel(item.status) }}
              </CBadge>
            </td>
          </template>

          <!-- Fattura -->
          <template #fattura="{item}">
            <td>
              <div v-if="item.invoice">
                <CLink 
                  :href="item.invoice" 
                  target="_blank"
                  class="d-flex align-items-center"
                >
                  <CIcon name="cipFile03" size="sm" class="mr-1"/>
                  {{ item.invoice_number || 'Fattura' }}
                </CLink>
              </div>
              <div v-else-if="item.status === 'requested'">
                <CButton
                  color="warning"
                  size="sm"
                  @click="completePayout(item)"
                >
                  <CIcon name="cipUpload" size="sm" class="mr-1"/>
                  <translate translate-context="payout">Upload Invoice</translate>
                </CButton>
              </div>
              <div v-else-if="item.status === 'pending'" class="text-warning">
                <translate translate-context="payout">Waiting for request</translate>
              </div>
              <div v-else>
                <span class="text-muted">-</span>
              </div>
            </td>
          </template>

          <!-- Note -->
          <template #note="{item}">
            <td>
              <div v-if="item.notes" class="text-truncate" style="max-width: 200px;" :title="item.notes">
                {{ item.notes }}
              </div>
              <div v-else-if="item.status === 'requested'" class="text-info">
                <translate translate-context="payout">Invoice pending</translate>
              </div>
              <div v-else-if="item.status === 'pending'" class="text-muted">
                <translate translate-context="payout">Ready to request</translate>
              </div>
              <div v-else>
                <span class="text-muted">-</span>
              </div>
            </td>
          </template>

          <!-- Azioni -->
          <template #actions="{item}">
            <td>
              <div class="d-flex gap-2">
                <CButton
                  v-if="item.status === 'requested'"
                  color="warning"
                  size="sm"
                  variant="ghost"
                  @click="completePayout(item)"
                  v-c-tooltip="{content: $pgettext('payout', 'Upload Invoice')}"
                >
                  <CIcon name="cipUpload" size="lg" />
                </CButton>
                <CButton
                  color="info"
                  size="sm"
                  variant="ghost"
                  @click="viewDetails(item)"
                  v-c-tooltip="{content: $pgettext('payout', 'View details')}"
                >
                  <CIcon name="cipEye" size="lg" />
                </CButton>
                <CButton
                  v-if="item.consolidated_csv || item.id"
                  color="success"
                  size="sm"
                  variant="ghost"
                  @click="handleDownloadCsv(item)"
                  v-c-tooltip="{content: $pgettext('payout', 'Download Excel')}"
                >
                  <CIcon name="cipDownload" size="lg" />
                </CButton>
                <CButton
                  v-if="canDeletePayout(item)"
                  color="danger"
                  size="sm"
                  variant="ghost"
                  @click="handleDeletePayout(item)"
                  v-c-tooltip="{content: $pgettext('payout', 'Delete Payout')}"
                >
                  <CIcon name="cipTrashFull" size="lg" />
                </CButton>
              </div>
            </td>
          </template>

          <!-- Row details (expanded) -->
          <template #details="{item}">
            <CCollapse :show="isExpanded(item)">
              <CCard class="m-3 border">
                <CCardBody>
                  <h6 class="mb-3">
                    <translate translate-context="payout">Dettaglio eventi contribuenti</translate>
                  </h6>
                  <div class="text-muted small mb-3">
                    <translate translate-context="payout">Eventi che hanno contribuito a questo payout</translate>
                  </div>
                  
                  <CDataTable
                    :items="item.pica_services || []"
                    :fields="eventFields"
                    hover
                    small
                    bordered
                  >
                    <template #event_name="{item: event}">
                      <td>
                        <div class="font-weight-bold">{{ event.pica_service_name }}</div>
                        <div class="text-muted small">
                          <CIcon name="cipCalendar" size="sm" class="mr-1" />
                          {{ event.date | formatDate }}
                        </div>
                      </td>
                    </template>

                    <template #photos_sold="{item: event}">
                      <td class="text-center">
                        <div class="font-weight-bold">{{ event.photos_sold || 0 }}</div>
                        <div class="text-muted small">
                          <translate translate-context="payout">Foto vendute</translate>
                        </div>
                      </td>
                    </template>

                    <template #revenue="{item: event}">
                      <td class="text-right">
                        <div class="font-weight-bold text-success">
                          {{ event.paid_net | currency(currency) }}
                        </div>
                        <div class="text-muted small">
                          <translate translate-context="payout">Net amount</translate>
                        </div>
                      </td>
                    </template>

                    <template #csv_download="{item: event}">
                      <td class="text-center">
                        <CButton
                          v-if="event.csv_file || event.pica_service_id"
                          color="primary"
                          size="sm"
                          variant="ghost"
                          @click="handleDownloadServiceCsv(item.id, event.pica_service_id)"
                        >
                          <CIcon name="cipDownload" size="sm"/>
                        </CButton>
                      </td>
                    </template>
                  </CDataTable>
                </CCardBody>
              </CCard>
            </CCollapse>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

// Stati mappati correttamente dal backend
const PAYOUT_STATUS_LABELS = {
  'pending': 'Pending',
  'requested': 'Requested',
  'payed': 'Paid'
}

export default {
  name: 'PayoutHistoryTable',
  props: {
    payouts: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: 'EUR'
    }
  },
  data() {
    return {
      expandedRows: [],
      fields: [
        {
          key: 'data_richiesta',
          label: this.$pgettext('payout', 'DATA RICHIESTA'),
          _style: 'width: 200px'
        },
        {
          key: 'importo',
          label: this.$pgettext('payout', 'IMPORTO'),
          _style: 'width: 150px'
        },
        {
          key: 'stato',
          label: this.$pgettext('payout', 'STATO'),
          _style: 'width: 150px'
        },
        {
          key: 'fattura',
          label: this.$pgettext('payout', 'FATTURA'),
          _style: 'width: 200px'
        },
        {
          key: 'note',
          label: this.$pgettext('payout', 'NOTE')
        },
        {
          key: 'actions',
          label: '',
          _style: 'width: 50px',
          sorter: false,
          filter: false
        }
      ],
      eventFields: [
        {
          key: 'event_name',
          label: this.$pgettext('payout', 'Evento'),
          _style: 'width: 40%'
        },
        {
          key: 'photos_sold',
          label: this.$pgettext('payout', 'Foto'),
          _style: 'width: 15%',
          _classes: 'text-center'
        },
        {
          key: 'revenue',
          label: this.$pgettext('payout', 'Ricavo'),
          _style: 'width: 20%',
          _classes: 'text-right'
        },
        {
          key: 'csv_download',
          label: this.$pgettext('payout', 'Excel'),
          _style: 'width: 25%',
          _classes: 'text-center'
        }
      ]
    }
  },
  methods: {
    ...mapActions('payout', [
      'downloadExcel',
      'downloadServiceExcel',
      'deletePayout',
      'fetchPayoutStatistics'
    ]),
    getStatusLabel(status) {
      return PAYOUT_STATUS_LABELS[status] || status
    },
    getStatusColor(status) {
      const colors = {
        'pending': 'warning',
        'requested': 'info',
        'payed': 'success'
      }
      return colors[status] || 'secondary'
    },
    toggleExpand(item) {
      const index = this.expandedRows.indexOf(item.id)
      if (index > -1) {
        this.expandedRows.splice(index, 1)
      } else {
        this.expandedRows.push(item.id)
      }
    },
    isExpanded(item) {
      return this.expandedRows.includes(item.id)
    },
    viewDetails(item) {
      this.$emit('view-details', item)
    },
    completePayout(item) {
      this.$emit('complete-payout', item)
    },
    canDeletePayout(item) {
      return item.is_last_requested && this.hasPerm('payout.delete_requested')
    },
    async handleDeletePayout(item) {
      if (!this.canDeletePayout(item)) return

      await this.confirm({
        color: 'danger',
        title: this.$pgettext('payout', 'Elimina Payout'),
        message: this.$pgettext('payout', 'Sei sicuro di voler eliminare questo payout?'),
        cb_confirm: async () => {
          try {
            await this.deletePayout(item.id)
            this.$emit('payout-deleted')
            await this.notifySuccess(this.$pgettext('payout', 'Payout eliminato con successo'))
          } catch (error) {
            await this.notifyError(this.$pgettext('payout', 'Errore durante l\'eliminazione del payout'))
          }
        }
      })
    },
    async handleDownloadCsv(item) {
      try {
        await this.downloadExcel(item.id)
      } catch (error) {
        await this.notifyError('Errore nel download del Excel')
      }
    },
    async handleDownloadServiceCsv(payoutId, serviceId) {
      try {
        await this.downloadServiceExcel({payoutId, serviceId})
      } catch (error) {
        await this.notifyError('Errore nel download del Excel del servizio')
      }
    }
  }
}
</script>

<style scoped>
.payout-history-table .table td {
  vertical-align: middle;
}

.gap-2 {
  gap: 0.5rem;
}

.d-flex.gap-2 > * + * {
  margin-left: 0.5rem;
}
</style>